import { graphql, StaticQuery } from "gatsby";
import { inject, observer } from "mobx-react";
import React, { Component, createRef } from "react";
import { Icons } from "src/libs/assets";
import { tx, txWithComponent } from "src/libs/i18n";
import { ISettings } from "src/models";
import { UploadDocumentsStore } from "src/stores";
import ReCAPTCHA from "react-google-recaptcha";
import {
  Button,
  Checkbox,
  Filepicker,
  IFileType,
  Input,
  Modal,
  P,
} from "src/views/shared/components";
import * as styles from "./UploadDocumentsModal.module.scss";

export interface UploadDocumentsModalProps {
  uploadDocumentsStore?: UploadDocumentsStore;
  settings?: ISettings;
}

@inject("uploadDocumentsStore")
@observer
export class UploadDocumentsModalComponent extends Component<UploadDocumentsModalProps> {

  private reCaptchaRef = createRef<any>();

  executeReCaptcha = async () => {
    const {
      toggleReCaptcha,
      reCaptchaValid,
      scrollPos,
    } = this.props.uploadDocumentsStore;

    scrollPos();
    if (reCaptchaValid) {
      toggleReCaptcha();
    } else {
      const response = await this.reCaptchaRef.current.executeAsync();
      if (response?.length > 0) {
        toggleReCaptcha();
        this.reCaptchaRef.current.reset();
      }
    }
  };

  render() {
    const {
      formData,
      isOpen,
      closeModal,
      onUpload,
      isUploadValid,
      validate,
      setAndValidate,
      reCaptchaValid
    } = this.props.uploadDocumentsStore;
    const {
      termsAndConditionsUrl,
      privacyPolicyUrl,
    } = this.props.settings?.themeSettings?.themeSettingsFields;

    const allowedFileTypes: IFileType[] = ["application/pdf"];

    const filesDescription = tx("UploadDocumentsModal.filesDescription", {
      filetypes: allowedFileTypes
        .map((filetype) => tx(`filetypes.${filetype}`))
        .join(" "),
    });

    return (
      <Modal open={isOpen} title={tx("misc.upload")} onClose={closeModal}>
        <div className={styles.padding}>
          <h2 className={styles.title}>{tx("UploadDocumentsModal.title")}</h2>
          <P medium className={styles.description}>
            {tx("UploadDocumentsModal.description")}
          </P>
          <h3 className={styles.sectionTitle}>{tx("misc.yourEmail")}</h3>
          <div className={styles.inputRow}>
            <img src={Icons.email_small} alt="email-icon" />
            <Input
              type="email"
              label="E-mail:"
              placeholder="E-mail:"
              htmlFor="upload_collection_email"
              value={formData.email.value}
              onChange={(value) => (formData.email.value = value)}
              onBlur={() => validate(formData.email)}
              error={formData.email.valid === false}
            />
          </div>

          <h3 className={styles.sectionTitle}>{tx("misc.description")}</h3>
          <div className={styles.inputRow}>
            <img src={Icons.file_text_small} alt="title-icon" />
            <Input
              type="text"
              label={tx("misc.documentTitle")}
              placeholder={tx("misc.documentTitle")}
              htmlFor="upload_collection_title"
              value={formData.title.value}
              onChange={(value) => (formData.title.value = value)}
              onBlur={() => validate(formData.title)}
              error={formData.title.valid === false}
            />
          </div>

          <div className={styles.inputRow}>
            <div />
            <Input
              type="textarea"
              label={tx("misc.summary")}
              placeholder={tx("misc.summary")}
              htmlFor="upload_collection_summary"
              value={formData.abstract.value}
              onChange={(value) => (formData.abstract.value = value)}
              onBlur={() => validate(formData.abstract)}
            />
          </div>

          <h3 className={styles.sectionTitle}>{tx("misc.upload")}</h3>
          <P small className={styles.sectionTitleDescription}>
            {filesDescription}
          </P>
          <Filepicker
            files={formData.en_files.value}
            onFilesUpdated={(files) => setAndValidate(formData.en_files, files)}
            allowedFileTypes={allowedFileTypes}
          />

          <h3 className={styles.sectionTitle}>{tx("misc.uploadPolicy")}</h3>
          <p
            className={styles.policy}
            dangerouslySetInnerHTML={{
              __html: tx("UploadDocumentsModal.policy"),
            }}
          />
        </div>

        <div className={styles.padding}>
          <div className={styles.checkbox}>
              <Checkbox
                type="check"
                onChange={this.executeReCaptcha}
                selected={reCaptchaValid}
              />
              <P className={styles.checkboxDescription} small>
                {tx("authentication.imNotARobot")}
              </P>
              <img
                src={Icons.reCaptcha}
                alt="reCaptcha-icon"
                className={styles.reCaptchaIcon}
              />
            </div>
            <ReCAPTCHA
              ref={this.reCaptchaRef}
              sitekey="6LfPzuggAAAAAPeARPBiQ_qFrsMeW_XsxvcMZLdb"
              size="invisible"
            />
          <div className={styles.checkbox}>
            <Checkbox
              type="check"
              selected={formData.approvedPolicy.value}
              onChange={(checked) =>
                setAndValidate(formData.approvedPolicy, checked)
              }
            />
            <P className={styles.checkboxDescription}>
              {tx("UploadDocumentsModal.approvedPolicy")}
            </P>
          </div>
          <div className={styles.checkbox}>
            <Checkbox
              type="check"
              selected={formData.consentTerms.value}
              onChange={(checked) =>
                setAndValidate(formData.consentTerms, checked)
              }
            />
            <P className={styles.checkboxDescription}>
              {txWithComponent("settings.inputs.termsApprovalText", [
                <a
                  href={termsAndConditionsUrl}
                  target="_blank"
                  ref="noreferrer"
                  key={"terms1"}
                >
                  {tx("settings.inputs.termsAndConditions")}
                </a>,
              ])}
            </P>
          </div>
          <Button
            className={styles.submitButton}
            size="large"
            color="red"
            onClick={onUpload}
            disabled={!isUploadValid}
          >
            {tx("UploadDocumentsModal.submit")}
          </Button>
        </div>
      </Modal>
    );
  }
}

export const UploadDocumentsModal = (props: UploadDocumentsModalProps) => (
  <StaticQuery
    query={query}
    render={(data) => {
      return (
        <UploadDocumentsModalComponent
          {...props}
          settings={data?.siteSettings}
        />
      );
    }}
  />
);

const query = graphql`
  query {
    ...siteSettingsFragment
  }
`;
